import React, { useState } from "react";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/macro";
import { graphql, PageProps } from "gatsby";
import {
  AsCardFragment,
  ShipDiningAreaModalFragment,
  ShipFacilityModalFragment,
  ShipQuery
} from "types/graphql-types";

import {
  Button,
  DescriptionTextColumnContent,
  DescriptionTextColumnContentLeft,
  DescriptionTextColumnContentRight,
  DescriptionTextColumnLeadIn,
  HeroBanner,
  HeroBannerRightPanel,
  HeroBannerTitle,
  HeroBannerAction
} from "@silversea-ssc/ui-components-react";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import getUrlsByLanguage from "src/urls/urls";
import FindYourCruise from "components/FindYourCruise";
import { DescriptionTextColumn } from "@silversea-ssc/ui-components-react";
import { ResponsiveSlideshow } from "components/Slideshow/Slideshow";
import { SectionContainer } from "@silversea-ssc/ui-components-react";
import { useFYC } from "components/FindYourCruise/useFYC";
import SectionTabs, {
  SectionTabsTab,
  SectionTabsTabList,
  SectionTabsTabPanel
} from "components/SectionTabs/SectionTabs";
import { MosaicLayout } from "@silversea-ssc/ui-components-react";
import ShipFacilityModal from "components/ShipFacilityModal/ShipFacilityModal";
import ItemList from "components/ItemList/ItemList";
import OpenGraph from "components/SEO/OpenGraph";
import { snakeCase } from "src/utils/strings";
import ShipSuiteModal from "components/ShipFacilityModal/ShipSuiteModal";
import FYCQueryBuilder from "components/FindYourCruise/FYCQueryBuilder";
import { ButtonsContainer } from "@silversea-ssc/ui-components-react";
import TextBox from "components/TextBox/TextBox";
import { isPresent } from "src/utils/checks";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import LinkCard from "components/Card/LinkCard";

const InternalFacilityCard: React.FC<{
  card: AsCardFragment;
  openModal: () => void;
}> = ({ card, openModal }) => {
  const trackingName = card?.trackingName?.en
    ? snakeCase(card?.trackingName?.en)
    : undefined;
  return (
    <LinkCard
      backgroundColor="neutral05"
      trackingName={trackingName}
      image={card.image}
      imageRatio="card-panorama"
      imageAltText={card?.title?.localized}
      title={card?.title?.localized}
      description={<p>{card?.description?.localized}</p>}
      onClick={openModal}
    />
  );
};

const ShipTemplate: React.FC<PageProps<ShipQuery>> = (props) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const ship = props.data.ship!;

  const geolocation = useGeolocation();
  const language = useLanguage();
  const urls = getUrlsByLanguage(language.code);
  const fycQuery = new FYCQueryBuilder()
    .filterByShipName(ship?.name)
    .generate();

  const {
    searchClient,
    filterDefaultSearch,
    searchState,
    createURL,
    onSearchStateChange,
    localizedSearchIndexName
  } = useFYC(fycQuery, geolocation.defaultCurrency);
  const { customBrochuresLink } = ship;
  const suites = ship.suites?.filter(isPresent);
  const diningAreas = ship.diningAreas?.filter(isPresent);
  const facilities = ship.facilities?.filter(isPresent);

  const brochuresUrl =
    customBrochuresLink?.localized || urls.serviceLinks.brochures.href;

  const [openedModal, setOpenModal] = useState<
    | {
        type: "suite";
        suiteCode?: string;
      }
    | {
        type: "facility";
        content: ShipFacilityModalFragment;
      }
    | {
        type: "diningArea";
        content: ShipDiningAreaModalFragment;
      }
    | undefined
  >(undefined);

  const [tabIndex, setTabIndex] = useState(0);
  const filteredSlideshow = ship.slideshow?.filter(isPresent);

  return (
    <React.Fragment>
      <OpenGraph
        openGraph={ship?.metadata?.openGraph}
        excludeFromSitemap={ship?.metadata?.excludeFromSitemap}
      />
      <HeroBanner background={ship.heroImage}>
        <HeroBannerRightPanel>
          <HeroBannerTitle>{ship.name}</HeroBannerTitle>
          <HeroBannerAction>
            <ButtonsContainer align="left" mobileFullSize>
              <Button
                variant="secondary"
                href={brochuresUrl}
                data-content-name="hero"
                data-content-piece="view_the_brochures"
              >
                <Trans>View the Brochures</Trans>
              </Button>

              <Button
                variant="primary"
                renderAs="button"
                onClick={() => {
                  setTabIndex(0);
                  if (typeof window["smoothscroll"] !== "undefined") {
                    const scroll = new window["smoothscroll"]();
                    const anchor = document.querySelector("#find-your-cruise");
                    scroll.animateScroll(anchor);
                  }
                }}
                data-content-name="hero"
                data-content-piece="view_voyages"
              >
                <Trans>View Voyages</Trans>
              </Button>
            </ButtonsContainer>
          </HeroBannerAction>
        </HeroBannerRightPanel>
      </HeroBanner>

      <SectionContainer
        spacingTop="regular"
        spacingBottom="regular"
        backgroundColor="neutral05"
        dataContentName="description_text"
      >
        <DescriptionTextColumn>
          <DescriptionTextColumnLeadIn>
            {ship.description?.leadIn?.localized}
          </DescriptionTextColumnLeadIn>
          <DescriptionTextColumnContent>
            <DescriptionTextColumnContentLeft>
              <TextBox
                fontSize="medium"
                content={ship.description?.content?.localized}
              />

              <br />
              <ItemList>
                {ship.passengerCount && (
                  <ItemList.Item>
                    <Trans>Guests capacity</Trans>:{" "}
                    <strong>{ship.passengerCount}</strong>
                  </ItemList.Item>
                )}
                {ship.crewCount && (
                  <ItemList.Item>
                    <Trans>Crew capacity</Trans>:{" "}
                    <strong>{ship.crewCount}</strong>
                  </ItemList.Item>
                )}
                {ship.buildYear && (
                  <ItemList.Item>
                    <Trans>Built</Trans>: <strong>{ship.buildYear}</strong>
                  </ItemList.Item>
                )}
                {ship.lastRefurbishmentYear && (
                  <ItemList.Item>
                    <Trans>Last refurbishment</Trans>:{" "}
                    <strong>{ship.lastRefurbishmentYear}</strong>
                  </ItemList.Item>
                )}
                {ship.tonnage && (
                  <ItemList.Item>
                    <Trans>Tonnage</Trans>:{" "}
                    <strong>
                      {i18n.number(ship.tonnage)} <Trans>Tons</Trans>
                    </strong>
                  </ItemList.Item>
                )}
              </ItemList>
              <ButtonsContainer spacingTop="regular" align="left">
                <Button
                  renderAs="a"
                  variant="primary"
                  href={`${ship.deckPlan?.asset?.url}?dl=`}
                  target="_blank"
                  rel="noreferrer"
                  data-content-piece="download_deck_plan"
                  data-content-params={{ shipName: ship.name }}
                >
                  <Trans>Deck plan</Trans>
                </Button>
              </ButtonsContainer>
            </DescriptionTextColumnContentLeft>

            <DescriptionTextColumnContentRight>
              {filteredSlideshow && (
                <ResponsiveSlideshow
                  items={filteredSlideshow}
                  variant="white"
                  fullscreen
                />
              )}
            </DescriptionTextColumnContentRight>
          </DescriptionTextColumnContent>
        </DescriptionTextColumn>
      </SectionContainer>

      <div id="find-your-cruise">
        <SectionTabs
          data-content-name="tab"
          selectedIndex={tabIndex}
          onSelect={(index) => setTabIndex(index)}
        >
          <SectionTabsTabList spacingTop="regular">
            <SectionTabsTab icon="voyages" data-content-piece="tab_voyages">
              <Trans>Voyages</Trans>
            </SectionTabsTab>
            <SectionTabsTab icon="suites" data-content-piece="tab_suites">
              <Trans>Suites</Trans>
            </SectionTabsTab>
            <SectionTabsTab icon="dining" data-content-piece="tab_dining">
              <Trans>Dining</Trans>
            </SectionTabsTab>
            <SectionTabsTab
              icon="public-area"
              data-content-piece="tab_public_areas"
            >
              <Trans>Public Areas</Trans>
            </SectionTabsTab>
          </SectionTabsTabList>
          <SectionTabsTabPanel
            data-content-name="tab_content_voyages"
            forceRender={true}
          >
            <FindYourCruise
              showTitle={false}
              backgroundColor="neutral10"
              searchClient={searchClient}
              initialSearchQuery={filterDefaultSearch}
              resultsPageSize={8}
              defaultSearchIndexName={localizedSearchIndexName}
              searchState={searchState}
              onSearchStateChange={onSearchStateChange}
              createURL={createURL}
              showFreeTextSearch={true}
              showExpeditionFilter={true}
              specialTypeFilter={undefined}
              showDestinationFilter={true}
              showShipFilter={false}
              showDurationFilter={true}
              showOthersFilter={true}
              showPromosFilter={true}
            />
          </SectionTabsTabPanel>
          <SectionTabsTabPanel data-content-name="tab_content_suites">
            {suites && (
              <SectionContainer
                spacingTop="regular"
                spacingBottom="regular"
                backgroundColor="white"
              >
                <MosaicLayout variant="column2">
                  {suites.map((suite) => {
                    if (!suite.card) return null;
                    return (
                      <InternalFacilityCard
                        key={suite._key}
                        card={suite.card}
                        openModal={() =>
                          setOpenModal({
                            type: "suite",
                            suiteCode: suite.codes ? suite.codes[0] : undefined
                          })
                        }
                      />
                    );
                  })}
                </MosaicLayout>
              </SectionContainer>
            )}
          </SectionTabsTabPanel>
          <SectionTabsTabPanel data-content-name="tab_content_dining">
            {diningAreas && (
              <SectionContainer
                spacingTop="regular"
                spacingBottom="regular"
                backgroundColor="white"
              >
                <MosaicLayout variant="column2">
                  {diningAreas.map((diningArea) => {
                    if (!diningArea.card) return null;
                    return (
                      <InternalFacilityCard
                        key={diningArea._key}
                        card={diningArea.card}
                        openModal={() =>
                          setOpenModal({
                            type: "diningArea",
                            content: diningArea
                          })
                        }
                      />
                    );
                  })}
                </MosaicLayout>
              </SectionContainer>
            )}
          </SectionTabsTabPanel>
          <SectionTabsTabPanel data-content-name="tab_content_public_areas">
            {facilities && (
              <SectionContainer
                spacingTop="regular"
                spacingBottom="regular"
                backgroundColor="white"
              >
                <MosaicLayout variant="column2">
                  {facilities.map((facility) => {
                    if (!facility.card) return null;
                    return (
                      <InternalFacilityCard
                        key={facility._key}
                        card={facility.card}
                        openModal={() =>
                          setOpenModal({ type: "facility", content: facility })
                        }
                      />
                    );
                  })}
                </MosaicLayout>
              </SectionContainer>
            )}
          </SectionTabsTabPanel>
        </SectionTabs>
      </div>

      {openedModal && openedModal.type == "suite" && (
        <ShipSuiteModal
          modal={{
            isOpen: !!openedModal,
            onRequestClose: () => setOpenModal(undefined)
          }}
          size="big"
          shipPath={props.location.pathname}
          suiteCode={openedModal.suiteCode}
        />
      )}
      {openedModal && openedModal.type == "facility" && (
        <ShipFacilityModal
          modal={{
            isOpen: !!openedModal,
            onRequestClose: () => setOpenModal(undefined)
          }}
          size="big"
          type={openedModal.type}
          content={openedModal.content}
        />
      )}
      {openedModal && openedModal.type == "diningArea" && (
        <ShipFacilityModal
          modal={{
            isOpen: !!openedModal,
            onRequestClose: () => setOpenModal(undefined)
          }}
          size="big"
          type={openedModal.type}
          content={openedModal.content}
        />
      )}
    </React.Fragment>
  );
};

export const query = graphql`
  query Ship($id: String!) {
    ship: sanityShip(id: { eq: $id }) {
      metadata {
        excludeFromSitemap
        openGraph {
          ...OpenGraphMetaTags
        }
      }

      name

      heroImage: image {
        ...HeroResponsiveImage
      }

      customBrochuresLink {
        localized
      }

      description {
        content {
          localized
        }
        leadIn {
          localized
        }
      }

      slideshow {
        ...SlideshowWithVideo
      }

      shipType
      suiteCount
      passengerCount
      crewCount
      buildYear
      lastRefurbishmentYear
      tonnage

      deckPlan {
        asset {
          url
        }
      }
      diningOptions

      suites {
        _key
        codes
        card {
          ...AsCard
        }
      }

      facilities {
        _key
        card {
          ...AsCard
        }

        ...ShipFacilityModal
      }

      diningAreas {
        _key
        card {
          ...AsCard
        }

        ...ShipDiningAreaModal
      }
    }
  }
`;

export default React.memo(ShipTemplate);
